// ============================================================
// Ranksector blog — shared config
//
// The /blog route on ranksector.com is powered by the same autopilot
// pipeline used for customer sites. To plug into it without inventing
// a parallel system, we treat ranksector.com itself as a "site" in
// Supabase with a hardcoded UUID. Any code path that needs to
// distinguish "is this our own blog?" reads from this module.
// ============================================================

/**
 * Hardcoded UUID for the ranksector.com "site" row in Supabase.
 * Inserted by migration 078_ranksector_blog_site.sql. Keep in sync
 * with that migration — the value is referenced both at SQL time
 * (for the seed inserts) and at runtime (for code paths that switch
 * behavior when serving our own blog).
 *
 * Hardcoded rather than env-var because (a) it never changes per
 * environment — staging and prod both point at the same logical
 * site, just different DBs, and (b) using an env var would mean
 * the migration couldn't reference it cleanly.
 */
export const RANKSECTOR_BLOG_SITE_ID = "c4e1d8a0-7b5d-4f8e-9a1b-2c3d4e5f0001";

/**
 * The system user that owns the ranksector site. Also inserted by
 * migration 078. Standalone — no auth.users link, no password — the
 * autopilot pipeline only needs sites.user_id to satisfy the FK.
 */
export const RANKSECTOR_BLOG_USER_ID = "c4e1d8a0-7b5d-4f8e-9a1b-2c3d4e5f0002";
export const RANKSECTOR_BLOG_USER_EMAIL = "blog-system@ranksector.com";

/**
 * Author name that appears under every blog post card and at the top
 * of every detail page. Hardcoded for the initial release — if we
 * later want author rotation (the LocalBlogAdapter could pick from a
 * pool), set article_settings.authorPool on the site row and read
 * from there instead.
 */
export const RANKSECTOR_BLOG_AUTHOR = "Ranksector team";

/**
 * Convert an article title into a URL slug suitable for /blog/{slug}.
 * Mirrors the slug shape we already store in articles.slug for other
 * sites — lowercase, hyphenated, alphanumeric only, max 80 chars.
 *
 * Used by:
 *   - LocalBlogAdapter.createDraft to populate articles.slug
 *   - The seed-ranksector-blog.mjs script for any pre-computed slugs
 */
export function slugifyTitle(title: string): string {
    return title
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")        // strip diacritics
        .replace(/[^a-z0-9]+/g, "-")             // non-alphanumerics -> hyphens
        .replace(/^-+|-+$/g, "")                 // trim leading/trailing hyphens
        .slice(0, 80)
        .replace(/-+$/, "");                     // re-trim if slice hit a hyphen
}

/**
 * Build the canonical public URL for a blog post given its slug.
 * Used in sitemap, RSS (future), and the LocalBlogAdapter's
 * `published_url` field.
 */
export function blogPostUrl(slug: string): string {
    return `/blog/${slug}`;
}

/**
 * Human-readable category label for a blog article. Combines two
 * signals because content_type alone (the LLM-emitted shape) doesn't
 * carry enough information to pick the right bucket — most articles
 * end up labelled "guide" / "how-to" / "comparison", which all
 * collapsed to "SEO" under the previous one-signal heuristic.
 *
 * Title is the stronger signal: phrases like "WordPress" / "Webflow"
 * / "ChatGPT" / "case study" / "framework" map to a specific bucket
 * regardless of content_type. content_type is the fallback when the
 * title doesn't clearly belong to any of them.
 *
 * Default is "SEO" so a missing/unknown signal still lands somewhere.
 */
export function blogCategoryFromContentType(
    contentType: string | null | undefined,
    title?: string | null,
): string {
    const t = (title ?? "").toLowerCase();
    const ct = (contentType ?? "").toLowerCase();

    // Title signals — checked first because they're more specific
    // than the LLM-picked content_type taxonomy.
    if (/\bwordpress\b|\bwebflow\b|\bshopify\b|\bnotion\b|\bghost\b|\bwix\b|\bframer\b|\b(cms|publishing)\b/.test(t)) {
        return "Integrations";
    }
    if (/\bbacklink|\blink[- ]?build|\blink exchange|\bfooter link|\binternal link|\bcontextual link/.test(t)) {
        return "Backlinks";
    }
    if (/\bcase stud|\bsolo founder|\b\d+k monthly clicks|\bcustomer story|\bsuccess story|\bgrowth story/.test(t)) {
        return "Case Studies";
    }
    if (/\bai\b|\bchatgpt|\bllm\b|\bprompt|\bgemini|\bautomation|\bai[- ]written|\bai[- ]generated|\bclaude/.test(t)) {
        return "AI Content";
    }
    if (/\bworkflow\b|\bframework\b|\bprocess\b|\bstack\b|\bchecklist\b|\bplaybook\b|\bcadence\b|\bcalendar\b|\bsystem\b/.test(t)) {
        return "Playbooks";
    }

    // Fall back to content_type signals
    if (ct.includes("case") || ct.includes("study")) return "Case Studies";
    if (ct.includes("playbook") || ct.includes("workflow") || ct.includes("framework")) return "Playbooks";
    if (ct.includes("integration") || ct.includes("setup") || ct.includes("connect")) return "Integrations";
    if (ct.includes("backlink") || ct.includes("link")) return "Backlinks";
    if (ct.includes("ai") || ct.includes("prompt") || ct.includes("llm")) return "AI Content";
    return "SEO";
}

/**
 * Pick an image style for the blog article from the rotating pool.
 * Deterministic hash of the title so re-generating the same article
 * always lands on the same style — the public /blog visually varies
 * across topics but stays stable for any individual one.
 *
 * Pool excludes plain "infographic" because its prompt is more
 * editorial/explanatory and we want the blog to LOOK like a magazine
 * with multiple visual languages, not a documentation site.
 *
 * Used by inngest/autopilot.ts when site_id matches the ranksector
 * blog UUID — customer sites keep their explicit imageStyle setting.
 */
const BLOG_IMAGE_STYLE_POOL = [
    "cinematic",
    "watercolor",
    "sketch",
    "illustration",
    "infographic",
] as const;

export function pickRotatingBlogImageStyle(title: string): string {
    if (!title) return "infographic";
    // FNV-1a-ish hash — small, fast, no need for crypto
    let hash = 2166136261;
    for (let i = 0; i < title.length; i += 1) {
        hash ^= title.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    const idx = Math.abs(hash) % BLOG_IMAGE_STYLE_POOL.length;
    return BLOG_IMAGE_STYLE_POOL[idx];
}

/**
 * The canonical filter buckets shown on /blog. Order matters — used
 * to render the filter pill row left to right.
 */
export const BLOG_CATEGORIES = [
    "All",
    "SEO",
    "AI Content",
    "Playbooks",
    "Case Studies",
    "Backlinks",
    "Integrations",
] as const;

export type BlogCategory = (typeof BLOG_CATEGORIES)[number];

/**
 * Estimated read time from word count. Matches the convention used in
 * the placeholder articles (e.g. "8 MIN READ"). 200 wpm is the
 * standard editorial assumption for non-technical prose.
 */
export function estimateReadMinutes(wordCount: number | null | undefined): number {
    const w = Math.max(0, Number(wordCount ?? 0));
    return Math.max(1, Math.round(w / 200));
}
